// The board menu (Features 4, Tidy): the ⋯ in the board pill, with what is done to the board in view and used less
// than daily, so the pill keeps only its name, the boards list and All boards. In the one order every menu keeps.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Archive, CopyPlus, EyeOff, Maximize, Maximize2, MoreHorizontal, Palette, PanelLeft, PanelTop, PanelTopDashed, Pencil, RectangleHorizontal, RotateCcw, ScrollText, Shapes, Share2, SlidersHorizontal, SquarePlus, Trash2 } from "lucide-react";
import type { Board } from "../../../shared/types";
import { DEMO } from "../../demo/mode";
import { kindOf } from "../../../shared/boardKinds";
import { archiveBoard, copyBoard, deleteBoard, restoreBoard, setBoardTabs, setTabLook, switchBoard } from "../../state/actions";
import { boardItems, get, set, useStore } from "../../state/store";
import { camera } from "../../canvas/camera";
import { bounds, itemRect } from "../../canvas/geometry";
import { withMap } from "../../boards/mindmap/live";
import { openRailEditor } from "../../state/rail";
import { enterFullscreen } from "../Fullscreen";
import { cx } from "../../lib/util";
import { MenuList, type MenuEntry } from "./MenuList";
import { renameBoard } from "../../boards/Rename";
import { usePipeUi } from "../../boards/pipeline/state";

/** A Custom board's two copies, in every menu that has the board's rows: its app and records, or its app alone. */
export function copyRows(board: Board): MenuEntry[] {
  if (kindOf(board) !== "custom" || board.archived || DEMO) return [];
  return [
    { label: "Duplicate", group: "edit", icon: <CopyPlus size={14} />, run: () => void copyBoard(board.id, true) },
    { label: "New board from this", group: "edit", icon: <SquarePlus size={14} />, run: () => void copyBoard(board.id, false) },
  ];
}

/**
 * What the board menu holds for a board, so the command bar, a check and a right-click read the same rows. here: the
 * board is the one in view. For another board (a tab or the boards list right-clicked), what opens on a board (Share,
 * its look, its instructions, its columns) opens it first, and what only changes the view in front of you is left out.
 */
export function boardActions(board: Board, boards: number, here = true): MenuEntry[] {
  const k = kindOf(board);
  const canvas = k === "canvas" || k === "mindmap";
  const on = (run: () => void) => () => {
    if (!here) switchBoard(board.id);
    // from All boards, it closes first: it hides everything but itself, so a sheet opened behind it would not show
    if (get().panel === "boards") set({ panel: null, panelArg: null });
    run();
  };
  const del: MenuEntry = {
    label: "Delete",
    group: "delete",
    icon: <Trash2 size={14} />,
    danger: true,
    disabled: boards <= 1 && !board.archived,
    run: () => confirm(`Delete "${board.name}"? It stays in the trash for 30 days.`) && deleteBoard(board.id),
  };
  const full: MenuEntry = { label: "Full screen", group: "edit", icon: <Maximize2 size={14} />, kbd: "F", run: () => void enterFullscreen() };
  if (board.archived) return [{ label: "Restore", group: "main", icon: <RotateCcw size={14} />, run: () => restoreBoard(board.id) }, ...(here ? [full] : []), del];
  const out: MenuEntry[] = [
    // its name, on its title (Fixes 9): every kind of board
    { label: "Rename", group: "main", icon: <Pencil size={14} />, run: () => renameBoard(board.id) },
    { label: "Share", group: "main", icon: <Share2 size={14} />, note: board.share.enabled ? "On" : undefined, run: on(() => set({ panel: "share" })) },
    { label: "Look and themes", group: "main", icon: <Palette size={14} />, run: on(() => set({ panel: "theme" })) },
    { label: "Instructions for agents", group: "ask", icon: <ScrollText size={14} />, note: board.instructions ? "On" : undefined, run: on(() => set({ panel: "instructions", panelArg: null })) },
  ];
  // a pipeline's columns and currency (Fixes 9), from its menu too
  if (k === "pipeline") out.push({ label: "Stages and currency", group: "main", icon: <SlidersHorizontal size={14} />, run: on(() => usePipeUi.setState({ sheet: "stages" })) });
  if (canvas && here) out.push({ label: "See everything", group: "edit", icon: <Maximize size={14} />, kbd: "⇧1", run: () => camera.fit(bounds(withMap(boardItems(get()).map(itemRect))), { maxZ: 1 }) });
  out.push(...copyRows(board), ...(here ? [full] : []));
  if (here) out.push({ label: "Hide the buttons", group: "more", icon: <EyeOff size={14} />, kbd: "⇧F", run: () => set({ focusMode: true }) });
  if (k === "canvas" && here) out.push({ label: "Customize the toolbar", group: "more", icon: <PanelLeft size={14} />, run: () => openRailEditor() });
  out.push({ label: "Archive", group: "more", icon: <Archive size={14} />, disabled: boards <= 1, run: () => archiveBoard(board.id) });
  out.push(del);
  return out;
}

/**
 * How the boards show (Features 8; David, 1 Nov: easy to switch, always in view): tabs in the island where the pill
 * sits, tabs in a bar across the top, or the pill, and the boards' icons on the tabs or not. One setting for the account.
 */
export function tabsLookRows(): MenuEntry[] {
  const w = get().workspace;
  const tabs = !!w?.boardTabs;
  const bar = !!w?.tabLook?.bar;
  return [
    { label: "Tabs", group: "main", icon: <PanelTopDashed size={14} />, note: tabs && !bar ? "On" : undefined, run: () => (setTabLook({ bar: false }), setBoardTabs(true)) },
    { label: "Tabs in a bar", group: "main", icon: <PanelTop size={14} />, note: tabs && bar ? "On" : undefined, run: () => (setTabLook({ bar: true }), setBoardTabs(true)) },
    { label: "Pill, no tabs", group: "main", icon: <RectangleHorizontal size={14} />, note: tabs ? undefined : "On", run: () => setBoardTabs(false) },
    { label: "Icons on tabs", group: "edit", icon: <Shapes size={14} />, note: w?.tabLook?.noIcons ? undefined : "On", run: () => setTabLook({ icons: !!w?.tabLook?.noIcons }) },
  ];
}

/** The Tabs or pill button, in the pill and at the tabs' end: the way between the two, and the tabs' icons. */
export function TabsLook() {
  return <MenuButton label="Tabs or pill" icon={<PanelTop size={15} />} head="Show boards as" menuLabel="Show boards as" className="tabs-look-btn" menuClass="tabs-look-menu" rows={tabsLookRows} />;
}

/** The ⋯ beside All boards in the pill, and its menu under it. With tabs on, the same rows open from the open tab or a right-click (ContextMenu). */
export function BoardActions({ board }: { board: Board }) {
  const boards = useStore((s) => s.boards.length);
  return <MenuButton label="Board menu" icon={<MoreHorizontal size={15} />} head={board.name} menuLabel={`${board.name}: board menu`} className="board-more-btn" menuClass="board-more-menu" rows={() => boardActions(board, boards)} />;
}

/**
 * A button and its menu under it. The menu is drawn at the board's root, not inside the pill or the tabs' island: their
 * glass blurs, and a blur inside another blurs nothing, so the words behind would show through its rows (QA, 26 Oct).
 * Its tooltip goes while it is open, as the account's does.
 */
function MenuButton({ label, icon, head, menuLabel, className, menuClass, rows }: { label: string; icon: ReactNode; head: string; menuLabel: string; className: string; menuClass: string; rows: () => MenuEntry[] }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const open = !!at;
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => !btn.current?.contains(e.target as Node) && !menu.current?.contains(e.target as Node) && setAt(null);
    const shut = () => setAt(null);
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("resize", shut);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("resize", shut);
    };
  }, [open]);
  const toggle = () => {
    if (open) return setAt(null);
    const r = btn.current?.getBoundingClientRect();
    // under the button, lined up with its left, the pill's gap below it
    if (r) setAt({ x: Math.max(8, r.left - 6), y: r.bottom + 14 });
  };
  const root = btn.current?.closest(".board-root") ?? document.body;
  return (
    <>
      <button ref={btn} className={cx("icon-btn", className, open && "is-active")} aria-label={label} aria-haspopup="menu" aria-expanded={open} data-tip={open ? undefined : label} onClick={toggle}>
        {icon}
      </button>
      {at &&
        createPortal(
          <div ref={menu} className={cx("menu island", menuClass)} role="menu" aria-label={menuLabel} style={{ left: at.x, top: at.y }}>
            <div className="menu-head">
              <b>{head}</b>
            </div>
            <MenuList entries={rows()} onClose={() => setAt(null)} />
          </div>,
          root,
        )}
    </>
  );
}
